import React, { createContext, useContext, useState } from 'react';

interface PageLoadingContextType {
  isPageLoading: boolean;
  pageLoadingText: string;
  showPageLoader: (text?: string) => void;
  hidePageLoader: () => void;
  setPageLoadingText: (text: string) => void;
}

const PageLoadingContext = createContext<PageLoadingContextType | undefined>(undefined);

export const usePageLoading = () => {
  const context = useContext(PageLoadingContext);
  if (context === undefined) {
    throw new Error('usePageLoading must be used within a PageLoadingProvider');
  }
  return context;
};

interface PageLoadingProviderProps {
  children: React.ReactNode;
}

export const PageLoadingProvider: React.FC<PageLoadingProviderProps> = ({ children }) => {
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pageLoadingText, setPageLoadingText] = useState('Loading...');

  const showPageLoader = (text: string = 'Loading...') => {
    setPageLoadingText(text);
    setIsPageLoading(true);
  };

  const hidePageLoader = () => {
    setIsPageLoading(false);
  };

  const updatePageLoadingText = (text: string) => {
    setPageLoadingText(text);
  };

  const value = {
    isPageLoading,
    pageLoadingText,
    showPageLoader,
    hidePageLoader,
    setPageLoadingText: updatePageLoadingText,
  };

  return (
    <PageLoadingContext.Provider value={value}>
      {children}
    </PageLoadingContext.Provider>
  );
};